import { SalePoint } from 'src/app/interfaces/salePoints.interface';
import { GeoDistanceService } from './address-calculate-meters';
import { AddressTextParser } from './address-parsed';

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface AddressInputExtras {
  etiqueta?: string | null;
  indicaciones?: string | null;
}

export interface LegacyAddress {
  address: string;
  ciudad: string;
  cobertura: boolean;
  distancia: number;

  etiqueta: string;
  indicaciones: string;
  id: string;

  inventario: string;
  lat: number;
  lng: number;

  nomenclatura: string;
  numeroUno: string;
  numeroDos: string;
  numeroTres: string;

  principal: boolean;

  costoDomicilio?: number | null;
  salePointId?: string | null;
}

export interface BuildResult {
  address: LegacyAddress;
  nearestSalePoint: (SalePoint & { meters: number }) | null;
}

type GComp = google.maps.GeocoderAddressComponent;

export class AddressFactory {
  constructor(private readonly maxCoverageMeters: number = 5000) { }

  buildFromGoogleAddress(params: {
    addressText: string;
    pointGPS: GeoPoint;
    salePoints: SalePoint[];
    extras?: AddressInputExtras;
    components?: GComp[] | null;
  }): BuildResult {
    const { addressText, pointGPS, salePoints, extras, components } = params;

    const points = (salePoints ?? []).filter(
      sp => typeof sp.lat === 'number' && typeof sp.lng === 'number'
    );

    const nearest = points.length ? GeoDistanceService.nearest(pointGPS, points) : null;

    const distancia = nearest ? Math.round(nearest.meters) : Number.POSITIVE_INFINITY;
    const cobertura = distancia <= this.maxCoverageMeters;

    const inventario = (nearest as any)?.bodega || '';
    const salePointId = (nearest as any)?.id ?? null;

    const ciudad = this.buildCiudadFromComponents(components) || this.extractCityFallback(addressText) || '';
    const barrio = this.buildBarrioFromComponents(components) || '';
    const addressShort = this.buildAddressShort(addressText, components);
    const parsed = AddressTextParser.parse(addressShort);
    const routeInfo = this.parseRouteFromComponents(components);

    const nomenclatura = parsed.nomenclaturaBase || routeInfo.nomenclatura || '';
    const numeroUno = parsed.numeroUno || routeInfo.numeroUno || '';

    const numeroDos = parsed.numeroDos || this.extractNumeroDosFromText(addressShort) || '';

    const legacy: LegacyAddress = {
      id: this.createLegacyId(),

      address: addressShort,
      ciudad,
      cobertura,
      distancia,

      etiqueta: (extras?.etiqueta ?? '') as string,
      indicaciones: (extras?.indicaciones ?? '') as string,

      inventario,
      lat: pointGPS.lat,
      lng: pointGPS.lng,

      nomenclatura,
      numeroUno,
      numeroDos,
      numeroTres: barrio,

      principal: true,

      salePointId,
    };

    return { address: legacy, nearestSalePoint: nearest as any };
  }

  applyAsPrimary<T extends { principal?: boolean }>(
    existing: Array<T | null | undefined> | undefined,
    newAddress: T
  ): T[] {
    const list = (Array.isArray(existing) ? existing : []).filter(Boolean) as T[];
    for (const a of list) a.principal = false;
    newAddress.principal = true;

    const idx = list.findIndex((a: any) => a?.lat === (newAddress as any)?.lat && a?.lng === (newAddress as any)?.lng);
    if (idx >= 0) {
      list[idx] = newAddress;
      return list;
    }

    list.push(newAddress);
    return list;
  }

  // ---------------- helpers ----------------

  private buildAddressShort(addressText: string, components?: GComp[] | null): string {
    const raw = (addressText ?? '').trim();
    const first = raw.split(',')[0]?.trim() ?? '';

    if (/[0-9]/.test(first) || first.includes('#')) return first;

    const route = this.getCompLong(components, 'route');
    const streetNumber = this.getCompLong(components, 'street_number');

    if (route && streetNumber) return `${route} ${streetNumber}`.trim();
    if (route) return route.trim();

    return first || raw;
  }

  private parseRouteFromComponents(components?: GComp[] | null): { nomenclatura: string; numeroUno: string } {
    const route = this.getCompLong(components, 'route').trim();
    if (!route) return { nomenclatura: '', numeroUno: '' };

    const parts = route.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return { nomenclatura: parts[0] ?? '', numeroUno: '' };

    const nomenclatura = this.normalizeNom(parts[0]);
    const numeroUno = parts.slice(1).join(' ').toUpperCase();
    return { nomenclatura, numeroUno };
  }

  private normalizeNom(token: string): string {
    const t = (token ?? '').replace(/\./g, '').trim().toLowerCase();
    if (['cra', 'kr', 'cr', 'carrera'].includes(t)) return 'Carrera';
    if (['cl', 'cll', 'calle'].includes(t)) return 'Calle';
    if (['av', 'avda', 'avenida'].includes(t)) return 'Avenida';
    if (['dg', 'diagonal'].includes(t)) return 'Diagonal';
    if (['tv', 'trv', 'tr', 'transversal'].includes(t)) return 'Transversal';
    return token ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : '';
  }

  private extractNumeroDosFromText(text: string): string | null {
    const m = (text ?? '').match(/#\s*([0-9A-Za-z]+(?:-[0-9A-Za-z]+)?)/);
    return m?.[1]?.toUpperCase() ?? null;
  }

  private createLegacyId(): string {
    return String(Math.random());
  }

  private extractCityFallback(addressText: string): string | null {
    if (!addressText) return null;
    const parts = addressText.split(',').map(p => p.trim()).filter(Boolean);

    if (parts.length >= 4) return [parts[1], parts[2], parts[3]].filter(Boolean).join(', ');
    if (parts.length >= 3) return [parts[1], parts[2]].filter(Boolean).join(', ');
    return parts.length >= 2 ? parts[1] : null;
  }

  private getCompLong(components: GComp[] | null | undefined, type: string): string {
    const list = Array.isArray(components) ? components : [];
    return list.find(x => x.types?.includes(type))?.long_name ?? '';
  }

  private buildCiudadFromComponents(components: GComp[] | null | undefined): string {
    const city =
      this.getCompLong(components, 'locality') ||
      this.getCompLong(components, 'administrative_area_level_2') ||
      '';
    const dept = this.getCompLong(components, 'administrative_area_level_1') || '';
    const country = this.getCompLong(components, 'country') || '';
    return [city, dept, country].filter(Boolean).join(', ');
  }

  private buildBarrioFromComponents(components: GComp[] | null | undefined): string {
    return (
      this.getCompLong(components, 'neighborhood') ||
      this.getCompLong(components, 'sublocality_level_1') ||
      this.getCompLong(components, 'sublocality') ||
      ''
    );
  }
}
